import * as React from "react";
import { Info, Target } from "lucide-react";
import { Pill } from "@/components/ui/pill";
import { derivePlanAdherenceVerdict } from "@/features/oee/plan-adherence-verdict";
import { usePlanAdherenceBounds } from "@/hooks/useOrganizationPolicy";
import type { PlanAdherence, PlanAdherenceBreakdown } from "@/types/oee.types";
import { PlanAdherenceExplanationDialog } from "./PlanAdherenceExplanationDialog";

export interface PlanAdherenceKpiProps {
  planAdherence?: PlanAdherence;
  planAdherenceBreakdown?: PlanAdherenceBreakdown;
  variant?: "card" | "inline";
  periodLabel?: string;
}

const ESTIMATED_TOOLTIP = "Sem plano declarado (início/fim planejado ou quantidade) para comparar.";

const WRAP =
  "border-border-rd mt-1 flex items-center justify-between rounded-md border border-dashed px-3 py-2";
const LABEL = "text-text-secondary inline-flex items-center gap-1.5 text-[12px]";

export function PlanAdherenceKpi({
  planAdherence,
  planAdherenceBreakdown,
  variant = "card",
  periodLabel = "Período corrente",
}: PlanAdherenceKpiProps): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);
  const value = planAdherence?.value ?? null;
  const available = planAdherence != null && value !== null && planAdherence.basis !== null;
  const bounds = usePlanAdherenceBounds();
  const verdict = available
    ? derivePlanAdherenceVerdict(planAdherence.basis, value, planAdherence.overrunPct, bounds)
    : null;

  if (variant === "inline") {
    if (planAdherence == null) return null;
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Explicar aderência ao plano"
          className="rounded focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
        >
          {available && verdict ? (
            <Pill tone={verdict.tone} withDot>
              {verdict.label}
            </Pill>
          ) : (
            <Pill tone="neutral" withDot>
              Sem plano
            </Pill>
          )}
        </button>
        <PlanAdherenceExplanationDialog
          open={open}
          onClose={() => setOpen(false)}
          planAdherence={planAdherence}
          breakdown={planAdherenceBreakdown}
          periodLabel={periodLabel}
        />
      </>
    );
  }

  return (
    <div className={WRAP}>
      <span className={LABEL}>
        <Target className="h-3.5 w-3.5 text-text-tertiary" aria-hidden="true" />
        Aderência ao plano
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Explicar aderência ao plano"
          className="rounded p-0.5 text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
        >
          <Info className="h-3 w-3" />
        </button>
      </span>

      {available && verdict ? (
        <div className="flex items-center gap-2">
          <span className="font-mono text-[14px] font-semibold text-text-primary">
            {value.toFixed(1)}%
          </span>
          <Pill tone={verdict.tone} withDot>
            {verdict.label}
          </Pill>
        </div>
      ) : (
        <span
          className="inline-flex cursor-help items-center gap-1 text-[13px] text-text-tertiary"
          title={ESTIMATED_TOOLTIP}
          aria-label={`Aderência ao plano indisponível: ${ESTIMATED_TOOLTIP}`}
        >
          —
        </span>
      )}

      <PlanAdherenceExplanationDialog
        open={open}
        onClose={() => setOpen(false)}
        planAdherence={planAdherence}
        breakdown={planAdherenceBreakdown}
        periodLabel={periodLabel}
      />
    </div>
  );
}
